"use client";

import React, { useEffect, useRef, useState } from "react";
import { ImageWrapper, Text } from "ui-components";

import { JobResponseEntity } from "@/src/hooks/api/generated";
import { getOrganizationImages } from "@/utils/organization";

import { buildJobApplyUrl } from "../utils/buildJobApplyUrl";

import JobsDetailAction from "./jobsDetailAction";
import ToggleSaveJob from "./ToggleSaveJob";

export interface IFloatingApplyBarProps {
  jobDetails: JobResponseEntity;
}

export function FloatingApplyBar({
  jobDetails,
}: IFloatingApplyBarProps): JSX.Element {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<"static" | "sticky" | "absolute">(
    "static"
  );
  const barRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOverlappingAuth, setIsOverlappingAuth] = useState(false);
  const [isOverlappingFooter, setIsOverlappingFooter] = useState(false);
  const [isInDetailsSection, setIsInDetailsSection] = useState(true);
  const [barStyles, setBarStyles] = useState<React.CSSProperties>({});

  const { logoImageUrl } = getOrganizationImages(
    jobDetails?.organization,
    true
  );

  useEffect(() => {
    const handleScroll = (): void => {
      if (!barRef.current || !containerRef.current) {
        return;
      }
      const containerRect = containerRef.current.getBoundingClientRect();
      const isDesktop = window.innerWidth >= 768;

      if (isDesktop) {
        setBarStyles({
          width: containerRect.width,
          left: containerRect.left,
        });
      } else {
        setBarStyles({});
      }

      const scrollTop = window.scrollY;
      const initialThreshold = 300;

      if (scrollTop > initialThreshold) {
        setIsVisible(true);
        setPosition("sticky");
      } else {
        setIsVisible(false);
        setPosition("static");
      }

      const authSection = document.getElementById("authentication-section");
      const footerSection = document.getElementById("main-footer");
      const detailsSection = document.getElementById("job-details-section");

      const viewportHeight = window.innerHeight;
      const HIDE_THRESHOLD = 50;
      const barHeight = barRef.current?.offsetHeight || 80;

      if (authSection) {
        const rect = authSection.getBoundingClientRect();
        // On desktop bar is at top, hide only if section hits top
        // On mobile bar is at bottom, hide if section enters viewport
        const overlapPos = isDesktop ? barHeight : viewportHeight;
        setIsOverlappingAuth(rect.top < overlapPos - HIDE_THRESHOLD);
      }
      if (footerSection) {
        const rect = footerSection.getBoundingClientRect();
        const overlapPos = isDesktop ? barHeight : viewportHeight;
        setIsOverlappingFooter(rect.top < overlapPos - HIDE_THRESHOLD);
      }
      if (detailsSection) {
        const rect = detailsSection.getBoundingClientRect();
        // Hide only when the end of the details section reaches the bar
        const overlapPos = isDesktop ? barHeight : viewportHeight;
        setIsInDetailsSection(rect.bottom > overlapPos - HIDE_THRESHOLD);
      }
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  const getPositionClass = (): string => {
    if (position === "sticky") {
      return "fixed";
    }
    return "fixed md:static";
  };

  return (
    <div ref={containerRef} className="relative">
      <div
        ref={barRef}
        className={`w-full bg-white border-b border-gray-200 px-10 py-4 flex flex-col md:flex-row items-center justify-between z-50 transition-opacity duration-300 left-0 bottom-0 md:bottom-auto md:top-0 ${getPositionClass()} ${
          isVisible &&
          !isOverlappingAuth &&
          !isOverlappingFooter &&
          isInDetailsSection
            ? ""
            : "md:hidden md:pointer-events-none"
        }`}
        style={barStyles}
      >
        <div className="md:flex gap-4 items-center hidden flex-1 min-w-0 mr-4">
          {logoImageUrl ? (
            <div className="w-[68px] h-[68px] border border-gray-200 rounded-[10px] flex-shrink-0">
              <ImageWrapper
                src={logoImageUrl}
                width={68}
                height={68}
                alt={`${jobDetails?.organization?.name} logo`}
                className="rounded-[10px]"
                style={{
                  objectFit: "cover",
                }}
                isCLDImage
              />
            </div>
          ) : null}
          <div className="flex flex-col gap-2.5">
            <Text className="!font-bold !text-2xl leading-none">
              {jobDetails.title}
            </Text>
            <Text className="font-semibold text-lg leading-none">
              {jobDetails?.organization?.name}
            </Text>
          </div>
        </div>
        <div className="flex gap-4 items-center">
          <JobsDetailAction
            id={jobDetails.id}
            jobDetails={jobDetails}
            title={jobDetails.title}
            slug={jobDetails.slug}
            isApplyWithEmail={jobDetails?.organization.is_apply_via_email}
            applyEmail={jobDetails.organization.apply_email}
            applyUrl={buildJobApplyUrl(jobDetails)}
            souceInternalId={jobDetails.source_internal_id ?? ""}
            className="w-full md:w-auto whitespace-nowrap"
          />
          <ToggleSaveJob jobDetails={jobDetails} isInFloating />
        </div>
      </div>
    </div>
  );
}
